import { CalendarView } from '@/domains/calendar/components/CalendarView';
import type { CalendarTeam } from '@/domains/calendar/components/CalendarView';
import type { Job } from '@/domains/jobs/components/JobCard';
import {
  useScheduleWorkOrder,
  useUnscheduleWorkOrder,
} from '@/domains/jobs/hooks/useWorkOrderActions';
import {
  useCreatePlannerEvent,
  usePlannerResources,
  useScheduledJobs,
  useUnscheduledJobs,
} from '@/domains/planner';
import { queryKeys } from '@/lib/query-client';
import type { PlannerJob } from '@skewb-works/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

function mapPriority(name: string | null): 'low' | 'medium' | 'high' {
  const lower = (name ?? '').toLowerCase();
  if (lower === 'critical' || lower === 'high') return 'high';
  if (lower === 'medium') return 'medium';
  return 'low';
}

function mapPlannerJobToJob(pj: PlannerJob): Job {
  return {
    id: String(pj.id),
    title: pj.name ?? 'Untitled',
    description: pj.job_category_name ?? pj.description ?? '',
    status: 'pending',
    priority: mapPriority(pj.priority_name),
    location: [pj.address, pj.town, pj.pincode].filter(Boolean).join(', '),
    estimatedHours: pj.duration ?? 0,
    duration: pj.duration ?? undefined,
    tags: [pj.job_category_name, pj.client_name].filter(Boolean) as string[],
    client: pj.client_name ?? undefined,
    category: pj.job_category_name ?? undefined,
    coordinates: pj.coordinates ?? undefined,
    scheduledDate: pj.scheduled_date ?? undefined,
    scheduledEndDate: pj.scheduled_end_date ?? undefined,
    startTime: pj.schedule_start ?? undefined,
    endTime: pj.schedule_end ?? undefined,
    teamId: pj.team_id ? String(pj.team_id) : undefined,
    workOrderStatus: pj.work_order_status ?? undefined,
    permitRequired: pj.permit_required,
    reinstatementRequired: pj.reinstatement_required,
    permitStatusId: pj.permit_status_id ?? null,
    permitStatusName: pj.permit_status_name ?? null,
  };
}

export function SchedulePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: resources = [] } = usePlannerResources();
  const [searchTerm, setSearchTerm] = useState('');
  const { data: unscheduledData } = useUnscheduledJobs({
    search: searchTerm || undefined,
  });
  const { data: scheduledData } = useScheduledJobs({
    search: searchTerm || undefined,
  });
  const createEvent = useCreatePlannerEvent();
  const scheduleWO = useScheduleWorkOrder();
  const unscheduleWO = useUnscheduleWorkOrder();

  const jobs: Job[] = useMemo(() => {
    const unscheduled = (unscheduledData?.data ?? []).map(mapPlannerJobToJob);
    const scheduled = (scheduledData?.data ?? []).map(mapPlannerJobToJob);
    return [...unscheduled, ...scheduled];
  }, [unscheduledData, scheduledData]);

  const teams: CalendarTeam[] = useMemo(() => {
    const teamMap = new Map<number, { name: string; memberCount: number }>();
    for (const r of resources) {
      if (r.teamId && r.teamName) {
        const existing = teamMap.get(r.teamId);
        if (existing) {
          existing.memberCount++;
        } else {
          teamMap.set(r.teamId, { name: r.teamName, memberCount: 1 });
        }
      }
    }
    return Array.from(teamMap.entries()).map(([id, data]) => ({
      id: String(id),
      name: data.name,
      memberCount: data.memberCount,
    }));
  }, [resources]);

  const invalidatePlannerJobs = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.planner.all });
    await queryClient.refetchQueries({ queryKey: queryKeys.planner.all });
  }, [queryClient]);

  const handleUpdateJob = useCallback(
    async (jobId: string, updatedJob: Partial<Job>) => {
      const { scheduledDate, teamId, startTime, endTime } = updatedJob as {
        scheduledDate?: string;
        teamId?: string;
        startTime?: string;
        endTime?: string;
      };

      // Unschedule
      if (!scheduledDate || !teamId) {
        await unscheduleWO.mutateAsync(Number(jobId));
        await invalidatePlannerJobs();
        return;
      }

      // If already scheduled (reschedule), unschedule first to clean up old records
      const existingJob = jobs.find((j) => j.id === jobId);
      if (existingJob?.scheduledDate) {
        try {
          await unscheduleWO.mutateAsync(Number(jobId));
        } catch {
          // Job might not be in Planned status yet — continue with scheduling
        }
      }

      // Schedule — use provided start/end times or default to 08:00-09:00
      const dbTeamId = Number(teamId);
      const start = startTime ?? new Date(`${scheduledDate}T08:00:00`).toISOString();
      const end = endTime ?? new Date(`${scheduledDate}T09:00:00`).toISOString();
      const member = resources.find((r) => r.teamId === dbTeamId);
      if (!member) return;

      await createEvent.mutateAsync({
        userId: member.id,
        teamId: dbTeamId,
        start,
        end,
        jobId: Number(jobId),
      });

      await scheduleWO.mutateAsync(Number(jobId));
      invalidatePlannerJobs();
    },
    [createEvent, resources, scheduleWO, unscheduleWO, invalidatePlannerJobs, jobs],
  );

  return (
    <CalendarView
      jobs={jobs}
      onUpdateJob={handleUpdateJob}
      onJobClick={(jobId) => navigate(`/jobs/${jobId}`)}
      teams={teams}
      onSearchChange={setSearchTerm}
    />
  );
}
